<template>
  <div class="card">
    <section class="position-relative">
      <section v-if="mode === $options.modes.LIVE && participant.status !== $options.status.DISQUALIFIED">
        <span
          :class="[
            $style.live,
            'badge badge-danger text-uppercase d-flex align-items-center px-2 text-condensed'
          ]"
        >
          <RecordDot
            class="mr-2"
            color="white"
            recording
          />
          {{ $t('label.live') }}
        </span>
      </section>
      <RunMap
        v-if="showRunMap"
        :id="participant.id"
        :track="run"
        :class="$style.map"
        class="rounded-top"
      />
      <div
        v-else
        class="position-relative"
      >
        <img
          src="@/assets/lx-hero-runner.jpg"
          srcset="@/assets/lx-hero-runner.jpg 1x, @/assets/lx-hero-runner@2x.jpg 2x"
          alt="runners"
          width="400"
          height="237"
          class="img-fluid w-100 rounded-top"
        >
      </div>

      <div
        v-if="participant"
        class="d-flex flex-column align-items-center"
        style="margin-top:-2.5rem"
      >
        <Avatar
          :runner="participant"
          class="mb-3"
          style="z-index: 400"
        />
        <StatusBadge
          v-if="hasStatusBadge(participant.status)"
          :value="participant.status"
        />
      </div>
    </section>

    <section class="container my-3 text-uppercase text-condensed font-weight-bold">
      <div class="d-flex justify-content-between align-items-center mb-4">

        <h1 class="h2 mb-0 text-dark">{{ participant.fullname }}</h1>

        <PinboardStatusButton
          v-if="!participant.id.match(/^unknown/)"
          :id="participant.id"
          type="participant"
          class="ml-auto"
          icon
        />
      </div>

      <router-link
        v-if="participant.team"
        class="card my-2"
        :to="{ name: 'teams.detail', params: { id: participant.team.id } }"
      >
        <div class="card-body d-flex align-items-center">
          <img
            :src="participant.team.avatar || require('@/assets/avatar/location.gif')"
            :alt="participant.team.name"
            width="192"
            height="192"
            :class="[$style.avatar, 'rounded-circle mr-3']"
          >
          <div>
            <div
              v-t="'participant.team'"
              class="text-muted"
            />
            {{ participant.team.name }}
          </div>
        </div>
      </router-link>

      <router-link
        v-if="participant.location"
        class="card my-2"
        :to="{ name: 'locations.detail', params: { id: participant.location.id } }"
      >
        <div class="card-body d-flex align-items-center">
          <img
            v-if="participant.location.image"
            :src="participant.location.image"
            :alt="participant.location.title"
            width="48"
            height="48"
            :class="[$style.avatar, 'rounded-circle mr-3']"
          >
          <div>
            <div
              v-t="'participant.location'"
              class="text-muted"
            />
            <template>{{ participant.location.title }}</template>
            <span v-if="participant.location.webcode !== 'app'">, {{ participant.location.ioc }}</span>
          </div>
        </div>
      </router-link>
      <section class="text-center my-3">
        <h2
          class="text-muted"
          v-text="$t('social.profile')"
        />
        <SocialSharing :text="$t('lxmeta.participant.title', {name: participant.fullname})"/>
      </section>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PinboardStatusButton from '@/components/PinboardStatusButton.vue'
import Avatar from '@/components/Avatar.vue'
import StatusBadge from '@/components/StatusBadge.vue'
import SocialSharing from '@/components/SocialSharing.vue'
import RecordDot from '@/components/RecordDot.vue'
import countries from '@/mixins/countries'
import * as modes from '@/constants/modes'
import { status } from '@/constants/participant'
import { Poller } from '@/Poller'
import { WATCH_UPDATE_INTERVAL } from '@/constants/intervals'
import RunMap from '@/components/RunMap.vue'
import hasStatusBadge from '@/util/hasStatusBadge'
import { isEqual } from 'lodash-es'

export default {
  name: 'ParticipantHero2023',
  status,
  modes,
  components: {
    StatusBadge,
    SocialSharing,
    Avatar,
    PinboardStatusButton,
    RecordDot,
    RunMap
  },
  mixins: [countries],
  props: {
    /**
     * @type {import('@/types/Participant.js').Participant}
     */
    participant: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      run: undefined,
      poller: new Poller(async (path) => {
        if (this.shouldLoadMap) {
          try {
            const response = await fetch(
              `${process.env.BASE_URL}${process.env.VUE_APP_JSON_URL}runs/${path}`
            )
            if (response.ok && response.headers.get('content-type')
              .match(/application\/json/)) {
              return response.json()
            }
          } catch (error) {
            // TBD: should we indicate that there was an error fetching tracking information
          }
        }

        return undefined
      }, WATCH_UPDATE_INTERVAL)
        .on('response', (run) => {
          this.run = run
        })
        .on('error', (e) => {
          throw e
        })
    }
  },
  computed: {
    ...mapGetters([
      'mode',
      'hasFeature'
    ]),
    isRunning() {
      if (!this.participant) {
        return false
      }

      return this.participant.status === status.RUNNING
    },
    shouldLoadMap() {
      return this.isRunning && this.participant?.liveTracking?.path
    },
    showRunMap() {
      return this.shouldLoadMap && this.run
    }
  },
  watch: {
    participant: {
      immediate: true,
      handler(newParticipant, oldParticipant) {
        if (isEqual(newParticipant, oldParticipant)) {
          return
        }
        if (newParticipant.id !== oldParticipant?.id) {
          this.run = undefined
        }
        if (newParticipant.liveTracking?.path) {
          this.poller.fetch(newParticipant.liveTracking.path)
        }
      }
    }
  },
  activated() {
    if (this.participant.liveTracking && this.participant.liveTracking.path) {
      this.poller.resume(this.participant.liveTracking.path)
    }
  },
  deactivated() {
    this.poller.pause()
  },
  beforeDestroy() {
    this.poller.stop()
  },
  methods: {
    ...mapActions([
      'donate'
    ]),
    hasStatusBadge
  }
}
</script>

<style lang="scss" module>
.map {
  aspect-ratio: 375 / 200;
}

.live {
  position: absolute;
  top: map-get($spacers, 3);
  left: map-get($spacers, 3);
  z-index: 450; // make it stay over leaflet's map-pane
}

.avatar {
  width: 3rem;
  height: 3rem;
}

.distance {
  line-height: 1.1;

  sup {
    font-size: 35%;
    top: -1.125rem;
    left: .125rem;
  }
}
</style>
